import React, {
  useCallback,
  useMemo,
  useState,
  useEffect,
  useRef,
} from "react";
import useUrlParamState from "hooks/useUrlParamState";
import { EvServiceInstallationPages } from "./evServiceInstallationPages";
import { ServiceOfferingData } from "./formPages/serviceOfferingSetup";
import { ChargerConfigData } from "./formPages/chargerConfiguration";
import { InstallationConfigData } from "./formPages/installationConfiguration";
import { PaymentAndAssumptionsConfigData } from "./formPages/paymentsAndAssumptions";
import { TextBoxSelectionType } from "components/basic/textBoxSelection";
import { DEFAULT_AREA_SERVICED_KM } from "./formPages/serviceOfferingSetup/components/AreaServicedSelector/constants";
import {
  useGetEvChargerServiceListByVendorQuery,
  useMeQuery,
} from "generated/graphql";
import { useNavigate, useSearchParams } from "react-router-dom";
import { accountRole } from "pages/dashboard/dashboardFeatures";

export interface EvServiceInstallationData {
  serviceOfferingSetup: ServiceOfferingData;
  chargerConfig: ChargerConfigData;
  installationConfig: InstallationConfigData;
  paymentAndAssumptionsConfigData: PaymentAndAssumptionsConfigData;
}

export interface evInstallationService {
  evServiceInstallationData: EvServiceInstallationData;
  setEvServiceInstallationData: React.Dispatch<
    React.SetStateAction<EvServiceInstallationData>
  >;
}

const EvServiceInstallation: React.FC = () => {
  const [currentPage] = useUrlParamState("currentPage", "serviceOfferingSetup");
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [{ data: meData, fetching: meFetching }] = useMeQuery();

  // Redirect non-vendors away from this page
  useEffect(() => {
    if (!meFetching && accountRole(meData?.me) !== "vendor") {
      navigate("/dashboard/profile", { replace: true });
    }
  }, [meFetching, meData, navigate]);

  // Get serviceId from URL params
  const serviceId = searchParams.get("serviceId");
  const vendorId = meData?.me?.vendor?.id;

  // Fetch only this vendor's services so cross-vendor access is impossible
  const [{ data: serviceData, fetching: servicesFetching }] =
    useGetEvChargerServiceListByVendorQuery({
      variables: { vendorId: vendorId ?? 0 },
      pause: !serviceId || !vendorId,
    });

  const [evServiceInstallationData, setEvServiceInstallationData] =
    useState<EvServiceInstallationData>({
      serviceOfferingSetup: {
        supportedEVSETypes: [
          {
            id: 1,
            optionName: "Tesla",
            isChecked: false,
            type: TextBoxSelectionType.selection,
          },
          {
            id: 2,
            optionName: "Other",
            isChecked: false,
            type: TextBoxSelectionType.selection,
          },
          {
            id: 3,
            optionName: "NEMA 14-50 Plugs",
            isChecked: false,
            type: TextBoxSelectionType.selection,
          },
        ],
        buildingServiced: [
          {
            id: 1,
            optionName: "Detached Homes",
            isChecked: false,
            type: TextBoxSelectionType.selection,
          },
          {
            id: 2,
            optionName: "Semi-Detached Homes",
            isChecked: false,
            type: TextBoxSelectionType.selection,
          },
          {
            id: 3,
            optionName: "Townhouses",
            isChecked: false,
            type: TextBoxSelectionType.selection,
          },
          {
            id: 4,
            optionName: "Commercial/Industrial",
            isChecked: false,
            type: TextBoxSelectionType.selection,
          },
          {
            id: 5,
            optionName: "Condo/Apartment Complexes",
            isChecked: false,
            type: TextBoxSelectionType.selection,
          },
        ],
        areaServiced: DEFAULT_AREA_SERVICED_KM,
      },
      chargerConfig: {
        evseType: [
          {
            id: 1,
            optionName: "NEMA 14-50 Outlet Only (Indoor)",
            isChecked: false,
            type: TextBoxSelectionType.price,
            price: 250,
          },
          {
            id: 2,
            optionName: "NEMA 14-50 Outlet Only (Outdoor)",
            isChecked: false,
            type: TextBoxSelectionType.price,
            price: 250,
          },
          {
            id: 3,
            optionName: "Tesla Wall Connector with NEMA 14-50 (Indoor)",
            isChecked: false,
            type: TextBoxSelectionType.price,
            price: 250,
          },
          {
            id: 4,
            optionName: "Tesla Wall Connector with NEMA 14-50 (Outdoor)",
            isChecked: false,
            type: TextBoxSelectionType.price,
            price: 250,
          },
          {
            id: 5,
            optionName: "Tesla Wall Hardwired",
            isChecked: false,
            type: TextBoxSelectionType.price,
            price: 250,
          },
          {
            id: 6,
            optionName: "NEMA 14-50 With Level 2 Charger (Indoor)",
            isChecked: false,
            type: TextBoxSelectionType.nameAndPrice,
            price: 250,
            enteredName: "",
            duplicates: true,
            isTemplate: true,
            placeholder: "enter charger name",
          },
          {
            id: 7,
            optionName: "NEMA 14-50 With Level 2 Charger (Outdoor)",
            isChecked: false,
            type: TextBoxSelectionType.nameAndPrice,
            price: 250,
            enteredName: "",
            duplicates: true,
            isTemplate: true,
            placeholder: "enter charger name",
          },
          {
            id: 8,
            optionName: "Hardwired with Level 2 Charger",
            isChecked: false,
            type: TextBoxSelectionType.nameAndPrice,
            price: 250,
            enteredName: "",
            duplicates: true,
            isTemplate: true,
            placeholder: "enter charger name",
          },
        ],
      },
      installationConfig: {
        cableLength: [
          {
            id: 1,
            optionName: "10 FT",
            isChecked: false,
            type: TextBoxSelectionType.price,
            price: 100,
          },
          {
            id: 2,
            optionName: "20 FT",
            isChecked: false,
            type: TextBoxSelectionType.price,
            price: 100,
          },
          {
            id: 3,
            optionName: "30 FT",
            isChecked: false,
            type: TextBoxSelectionType.price,
            price: 100,
          },
          {
            id: 4,
            optionName: "40 FT",
            isChecked: false,
            type: TextBoxSelectionType.price,
            price: 100,
          },
          {
            id: 5,
            optionName: "50 FT",
            isChecked: false,
            type: TextBoxSelectionType.price,
            price: 100,
          },
        ],
        inclusions: [
          {
            id: 1,
            isChecked: false,
            optionName: "All Materials & Labour",
            type: TextBoxSelectionType.selection,
          },
          {
            id: 2,
            isChecked: false,
            optionName: "Electrical Inspection & Permit",
            type: TextBoxSelectionType.selection,
          },
          {
            id: 3,
            isChecked: false,
            optionName: "Complimentary Site Visit",
            type: TextBoxSelectionType.selection,
          },
          {
            id: 4,
            isChecked: false,
            optionName: "Custom Inclusion",
            type: TextBoxSelectionType.name,
            duplicates: true,
            isTemplate: true,
            placeholder: "[add custom inclusion]",
          },
        ],
        addOnServices: [
          {
            id: 1,
            optionName: "Service Upgrade: 200A Panel and Installation",
            isChecked: false,
            type: TextBoxSelectionType.price,
            price: 100,
          },
          {
            id: 2,
            optionName: "Service Upgrade: 400A Panel and Installation",
            isChecked: false,
            type: TextBoxSelectionType.price,
            price: 100,
          },
        ],
      },
      paymentAndAssumptionsConfigData: {
        paymentTypes: [
          {
            id: 1,
            optionName: "Credit Card",
            isChecked: false,
            type: TextBoxSelectionType.name,
            enteredName: "",
            placeholder: "insert link to payment",
          },
          {
            id: 2,
            optionName: "E-transfer",
            isChecked: false,
            type: TextBoxSelectionType.name,
            enteredName: "",
            placeholder: "insert email for e-transfers",
          },
          {
            id: 3,
            optionName: "Cheque",
            isChecked: false,
            type: TextBoxSelectionType.name,
            enteredName: "",
            placeholder: "insert payee name i.e., ABC Inc",
          },
          {
            id: 4,
            optionName: "Financing Options",
            isChecked: false,
            type: TextBoxSelectionType.name,
            enteredName: "",
            duplicates: true,
            isTemplate: true,
          },
        ],
        quoteValidFor: "2 Weeks",
        paymentTerms: "",
        assumptions: [
          {
            id: 1,
            isChecked: false,
            optionName:
              "Electrical panel has 2 spare breaker spaces (fuses not accepted)",
            type: TextBoxSelectionType.selection,
          },
          {
            id: 2,
            isChecked: false,
            optionName: "Work completed weekdays, 8:00 AM – 4:00 PM",
            type: TextBoxSelectionType.selection,
          },
          {
            id: 3,
            isChecked: false,
            optionName:
              "Additional charges may apply for fishing cable through an unfinished basement",
            type: TextBoxSelectionType.selection,
          },
          {
            id: 4,
            isChecked: false,
            optionName:
              "Electrical service can support EV load (no panel upgrade required)",
            type: TextBoxSelectionType.selection,
          },
          {
            id: 5,
            isChecked: false,
            optionName:
              "Indoor cable included; exterior TECK cable and connectors billed separately if required",
            type: TextBoxSelectionType.selection,
          },
          {
            id: 6,
            isChecked: false,
            optionName: "Custom Assumption",
            type: TextBoxSelectionType.name,
            duplicates: true,
            isTemplate: true,
            placeholder: "[add custom assumption]",
          },
        ],
      },
    });

  // Merges DB-saved (checked-only) fields back into the full default option list.
  // DB only persists checked items, so without this the edit form would only show
  // the previously selected options and hide all others.
  // Tracks whether we've already loaded DB data into the form.
  // Prevents useEffect from re-running (e.g. when servicesFetching cycles)
  // and overwriting the user's in-progress edits with stale DB state.
  const hasPopulatedRef = useRef(false);

  const mergeWithDefaults = useCallback(
    (defaults: any[], dbItems: any[] | undefined): any[] => {
      if (!dbItems?.length) {
        return defaults.map((item) => ({ ...item, isChecked: false }));
      }

      const result: any[] = [];

      for (const defaultItem of defaults) {
        if (defaultItem.duplicates) {
          // Duplicate items: push each saved clone, then always re-add the template at the bottom
          const dbMatches = dbItems.filter(
            (db) => db.optionName === defaultItem.optionName,
          );
          dbMatches.forEach((db) =>
            result.push({
              ...defaultItem,
              id: db.id,
              isChecked: db.isChecked,
              enteredName: db.enteredName || "",
              price: db.price ?? defaultItem.price,
              duplicates: true,
              isTemplate: false,
            }),
          );
          // Template always present at the bottom, always unchecked
          result.push({
            ...defaultItem,
            duplicates: true,
            isChecked: false,
            isTemplate: true,
          });
        } else {
          const dbMatch = dbItems.find(
            (db) => db.optionName === defaultItem.optionName,
          );
          result.push({
            ...defaultItem,
            isChecked: dbMatch ? dbMatch.isChecked : false,
            price: dbMatch?.price ?? defaultItem.price,
            enteredName: dbMatch?.enteredName || defaultItem.enteredName || "",
          });
        }
      }

      return result;
    },
    [],
  );

  // Effect to populate form data when editing existing service.
  // Only runs once — hasPopulatedRef prevents DB state from overwriting
  // user edits if the query re-fetches while the vendor is making changes.
  useEffect(() => {
    if (hasPopulatedRef.current) return;
    if (serviceId && !servicesFetching && serviceData !== undefined) {
      const foundService = serviceData?.getEVChargerServiceListByVendor?.find(
        (service: any) => service.id.toString() === serviceId,
      );

      // Service not found in this vendor's list — redirect
      if (!foundService) {
        navigate("/dashboard/serviceOfferings", { replace: true });
        return;
      }

      if (foundService.evChargerServiceFields) {
        const groupedFields = foundService.evChargerServiceFields.reduce(
          (acc: any, field: any) => {
            if (!acc[field.fieldName]) {
              acc[field.fieldName] = [];
            }
            acc[field.fieldName].push({
              id: field.id,
              optionName: field.fieldLabel,
              isChecked: field.isChecked,
              type: field.fieldType,
              price: field.price,
              enteredName: field.customLabel || "",
              duplicates: field.duplicates,
              placeholder: field.placeholder,
            });
            return acc;
          },
          {},
        );

        // Update the form data, merging DB selections back into the full default
        // option lists so all choices remain visible in edit mode
        hasPopulatedRef.current = true;
        setEvServiceInstallationData((prevData) => ({
          serviceOfferingSetup: {
            supportedEVSETypes: mergeWithDefaults(
              prevData.serviceOfferingSetup.supportedEVSETypes,
              groupedFields.supportedEVSETypes,
            ),
            buildingServiced: mergeWithDefaults(
              prevData.serviceOfferingSetup.buildingServiced,
              groupedFields.buildingServiced,
            ),
            areaServiced:
              foundService.areaServiced ??
              prevData.serviceOfferingSetup.areaServiced,
          },
          chargerConfig: {
            evseType: mergeWithDefaults(
              prevData.chargerConfig.evseType,
              groupedFields.evseType,
            ),
          },
          installationConfig: {
            cableLength: mergeWithDefaults(
              prevData.installationConfig.cableLength,
              groupedFields.cableLength,
            ),
            inclusions: mergeWithDefaults(
              prevData.installationConfig.inclusions,
              groupedFields.inclusions,
            ),
            addOnServices: mergeWithDefaults(
              prevData.installationConfig.addOnServices,
              groupedFields.addOnServices,
            ),
          },
          paymentAndAssumptionsConfigData: {
            paymentTypes: mergeWithDefaults(
              prevData.paymentAndAssumptionsConfigData.paymentTypes,
              groupedFields.paymentTypes,
            ),
            quoteValidFor:
              foundService.quoteValidFor ||
              prevData.paymentAndAssumptionsConfigData.quoteValidFor,
            paymentTerms:
              foundService.paymentTerms ||
              prevData.paymentAndAssumptionsConfigData.paymentTerms,
            assumptions: mergeWithDefaults(
              prevData.paymentAndAssumptionsConfigData.assumptions,
              groupedFields.assumptions,
            ),
          },
        }));
      }
    }
  }, [serviceId, serviceData, servicesFetching, navigate, mergeWithDefaults]);

  const currentPageComponent = useMemo(() => {
    return EvServiceInstallationPages.find(
      (page) => page.pageName === currentPage,
    );
  }, [currentPage]);

  return (
    <>
      {currentPageComponent?.component(
        evServiceInstallationData,
        setEvServiceInstallationData,
      )}
    </>
  );
};

export default EvServiceInstallation;
